import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { supabase } from '../../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../../config/constants';
import './WebChat.css';

const LOGO_URL = '/assets/images/3.webp';

function formatTime(dateStr) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Renderizar markdown simples (bold, code, listas)
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n- /g, '\n• ')
    .replace(/\n(\d+)\. /g, '\n$1. ');
  return html;
}

export default function WebChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  // Gerar ou recuperar session ID
  useEffect(() => {
    let sid = sessionStorage.getItem('webchat_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('webchat_session_id', sid);
    }
    setSessionId(sid);

    // Buscar nome do usuário
    try {
      const userData = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      setUserName(userData.nome || userData.email || 'Usuário');
    } catch {
      setUserName('Usuário');
    }
  }, []);

  // Carregar histórico quando abrir
  useEffect(() => {
    if (isOpen && sessionId && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen, sessionId]);

  // Scroll para baixo quando novas mensagens
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus no input quando abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen]);

  async function getAuthToken() {
    try {
      const { data } = await supabase.auth.getSession();
      return data?.session?.access_token || null;
    } catch {
      return null;
    }
  }

  async function loadHistory() {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/history/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.created_at
          })));
        }
      }
    } catch (err) {
      console.error('[WebChat] Erro ao carregar histórico:', err);
    }
  }

  const sendMessage = useCallback(async () => {
    const msg = input.trim();
    if (!msg || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setIsLoading(true);

    try {
      const token = await getAuthToken();
      if (!token) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Você precisa estar logado para usar o assistente.',
          timestamp: new Date().toISOString()
        }]);
        setIsLoading(false);
        return;
      }

      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: msg, session_id: sessionId })
      });

      if (!resp.ok) {
        throw new Error(`Erro ${resp.status}`);
      }

      const data = await resp.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      }]);
    } catch (err) {
      console.error('[WebChat] Erro:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, sessionId]);

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleNewSession() {
    const newSid = crypto.randomUUID();
    sessionStorage.setItem('webchat_session_id', newSid);
    setSessionId(newSid);
    setMessages([]);
  }

  // Auto-resize textarea
  function handleInputChange(e) {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  }

  const userInitial = userName ? userName.charAt(0).toUpperCase() : 'U';

  return (
    <>
      {/* Painel do Chat */}
      {isOpen && (
        <div className="webchat-panel">
          {/* Header */}
          <div className="webchat-header">
            <div className="webchat-header-avatar">
              <img src={LOGO_URL} alt="Halo" />
            </div>
            <div className="webchat-header-info">
              <div className="webchat-header-name">Halo</div>
            </div>
            <div className="webchat-header-actions">
              <button
                className="webchat-header-btn"
                onClick={handleNewSession}
                title="Nova conversa"
              >
                +
              </button>
            </div>
          </div>

          {/* Mensagens */}
          <div className="webchat-messages">
            {messages.length === 0 && !isLoading && (
              <div className="webchat-welcome">
                <div className="webchat-welcome-icon">
                  <img src={LOGO_URL} alt="Halo" />
                </div>
                <h3>Olá, {userName}!</h3>
                <p>
                  Sou o Halo, seu assistente. Posso ajudar com configurações,
                  consultar dados da sua empresa, ver métricas e muito mais.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`webchat-message ${msg.role}`}>
                <div className="webchat-msg-avatar">
                  {msg.role === 'assistant' ? (
                    <img src={LOGO_URL} alt="Halo" />
                  ) : (
                    userInitial
                  )}
                </div>
                <div>
                  <div
                    className="webchat-msg-bubble"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(msg.role === 'assistant'
                        ? renderMarkdown(msg.content)
                        : msg.content)
                    }}
                  />
                  <div className="webchat-msg-time">{formatTime(msg.timestamp)}</div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="webchat-typing">
                <div className="webchat-typing-avatar">
                  <img src={LOGO_URL} alt="Astro" />
                </div>
                <div className="webchat-typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="webchat-input-area">
            <textarea
              ref={textareaRef}
              className="webchat-input"
              placeholder="Digite sua mensagem..."
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="webchat-send-btn"
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              title="Enviar"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* Botão Flutuante */}
      <button
        className={`webchat-fab ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Fechar chat' : 'Abrir assistente Halo'}
      >
        {isOpen ? (
          <span className="webchat-fab-close">✕</span>
        ) : (
          <img src={LOGO_URL} alt="Halo" />
        )}
      </button>
    </>
  );
}
